/**
 * Bridge45 API client — org-scoped JWT auth.
 */

const BASE = (import.meta.env?.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '');
const SESSION_KEY = 'bridge45.session';

/** Client state path → REST resource. Order matters: longest match wins. */
export const COLLECTION_RESOURCES = {
  accounts: 'accounts',
  opportunities: 'opportunities',
  assetCandidates: 'asset-candidates',
  'd001.partners': 'partners',
  'd001.capital.conversations': 'capital-conversations',
  'infra.opportunities': 'infra-opportunities',
  'saudi.opportunities': 'saudi-opportunities',
  evidence: 'evidence',
  expenses: 'expenses',
  decisions: 'decisions',
  exceptions: 'strategy-exceptions',
  approvals: 'approvals',
  productRequests: 'product-requests',
  reviews: 'weekly-reviews',
  sprintReviews: 'sprint-reviews',
  top3: 'founder-actions',
  closeouts: 'closeouts',
  timeLog: 'time-log',
  bridgeBriefings: 'bridge-briefings',
  bridgeRecommendations: 'bridge-recommendations',
  bridgeAsks: 'bridge-asks',
  workPackages: 'work-packages',
  recommendationDecisions: 'recommendation-decisions',
  planVersions: 'plan-versions',
  planJobs: 'plan-jobs',
};

/** Client state path prefix → singleton document name. */
export const DOCUMENT_PATHS = {
  'infra.features': 'modules',
  meta: 'meta',
  settings: 'settings',
  runway: 'runway',
  d001: 'd001',
  saudi: 'saudi',
  plan: 'plan',
  planDefinition: 'planDefinition',
  gateReviews: 'gateReviews',
  ui: 'ui',
  planAutopilot: 'planAutopilot',
};

const COLLECTION_KEYS = Object.keys(COLLECTION_RESOURCES).sort((a, b) => b.length - a.length);
const DOCUMENT_KEYS = Object.keys(DOCUMENT_PATHS).sort((a, b) => b.length - a.length);

export function resourceForPath(path) {
  return COLLECTION_RESOURCES[path];
}

/** Resolves a write path onto either a collection or a document + inner path. */
export function routeForPath(path) {
  const exact = COLLECTION_RESOURCES[path];
  if (exact) return { kind: 'collection', resource: exact };

  for (const key of COLLECTION_KEYS) {
    if (path.startsWith(`${key}.`)) {
      return { kind: 'unsupported', reason: `${path} is inside the ${key} collection` };
    }
  }

  for (const key of DOCUMENT_KEYS) {
    if (path === key) return { kind: 'document', name: DOCUMENT_PATHS[key], inner: '' };
    if (path.startsWith(`${key}.`)) {
      return { kind: 'document', name: DOCUMENT_PATHS[key], inner: path.slice(key.length + 1) };
    }
  }

  return { kind: 'unsupported', reason: `No API route for "${path}"` };
}

export class ApiError extends Error {
  constructor(status, code, message, requestId) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.accessToken || !parsed?.orgId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function sessionHeaders() {
  const session = loadSession();
  const headers = {};
  if (session?.accessToken) {
    headers.authorization = `Bearer ${session.accessToken}`;
  }
  if (session?.orgId) {
    headers['x-org-id'] = session.orgId;
  }
  return headers;
}

function orgPrefix() {
  const session = loadSession();
  if (!session?.orgId) throw new ApiError(401, 'UNAUTHORIZED', 'Sign in required');
  return `/orgs/${session.orgId}`;
}

async function parseResponse(res, method, path) {
  if (res.status === 204) return null;
  const text = await res.text();
  const payload = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(
      res.status,
      error.code ?? 'UNKNOWN',
      error.message ?? `${method} ${path} failed with ${res.status}`,
      error.requestId,
    );
  }
  return payload;
}

function buildHeaders(auth, hasBody) {
  const headers = auth ? sessionHeaders() : {};
  if (hasBody) headers['content-type'] = 'application/json';
  else if (auth) delete headers['content-type'];
  return headers;
}

async function request(method, path, body, { auth = true } = {}) {
  const hasBody = body !== undefined;
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: buildHeaders(auth, hasBody),
      body: hasBody ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError(0, 'NETWORK', `Cannot reach the Bridge45 API: ${err.message}`);
  }

  if (res.status === 401 && auth && loadSession()?.refreshToken) {
    try {
      await refreshSession();
      res = await fetch(`${BASE}${path}`, {
        method,
        headers: buildHeaders(auth, hasBody),
        body: hasBody ? JSON.stringify(body) : undefined,
      });
    } catch {
      clearSession();
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — sign in again');
    }
  }

  return parseResponse(res, method, path);
}

function persistAuthResult(result) {
  const session = {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    orgId: result.orgId,
    userId: result.userId,
    email: result.email,
    displayName: result.displayName,
    memberRole: result.memberRole,
    organization: result.organization,
    profilePictureUrl: result.profilePictureUrl,
  };
  saveSession(session);
  notifySession(session);
  return session;
}

/** Merge fields into the current session (profile updates). */
export function patchSession(partial) {
  const current = loadSession();
  if (!current) return null;
  const next = { ...current, ...partial };
  saveSession(next);
  notifySession(next);
  return next;
}

function notifySession(session) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bridge45:session', { detail: session }));
  }
}

export async function signup({ email, password, displayName, orgName }) {
  const result = await request(
    'POST',
    '/auth/signup',
    { email, password, displayName, orgName },
    { auth: false },
  );
  return persistAuthResult(result);
}

export async function login({ email, password }) {
  const result = await request(
    'POST',
    '/auth/sessions',
    { email, password },
    { auth: false },
  );
  return persistAuthResult(result);
}

export async function refreshSession() {
  const current = loadSession();
  if (!current?.refreshToken) throw new ApiError(401, 'UNAUTHORIZED', 'No refresh token');
  const result = await request(
    'POST',
    '/auth/sessions/refresh',
    { refreshToken: current.refreshToken },
    { auth: false },
  );
  return persistAuthResult(result);
}

export function logout() {
  clearSession();
}

export const api = {
  getState: () => request('GET', `${orgPrefix()}/state`).then((r) => r.state),
  summary: () => request('GET', `${orgPrefix()}/state/summary`),
  importState: (state) => request('POST', `${orgPrefix()}/state/import`, { state }),

  createRecord: (resource, record) =>
    request('POST', `${orgPrefix()}/${resource}`, record),
  updateRecord: (resource, id, changes) =>
    request('PATCH', `${orgPrefix()}/${resource}/${id}`, changes),
  deleteRecord: (resource, id) =>
    request('DELETE', `${orgPrefix()}/${resource}/${id}`),
  replaceCollection: (resource, items) =>
    request('PUT', `${orgPrefix()}/${resource}`, { items }),

  putDocument: (name, value) =>
    request('PUT', `${orgPrefix()}/documents/${name}`, { value }),
  patchDocument: (name, path, value) =>
    request('PATCH', `${orgPrefix()}/documents/${name}`, { path, value }),

  audit: (limit = 100) => request('GET', `${orgPrefix()}/audit?limit=${limit}`),

  listMembers: () => request('GET', `${orgPrefix()}/members`),
  inviteMember: (body) => request('POST', `${orgPrefix()}/members`, body),

  getMe: () => request('GET', `${orgPrefix()}/profile/me`),
  updateMe: (body) => request('PATCH', `${orgPrefix()}/profile/me`, body),
  listBridgeProviders: () => request('GET', `${orgPrefix()}/bridge/providers`),
  putBridgeProvider: (id, body) => request('PUT', `${orgPrefix()}/bridge/providers/${id}`, body),
  testBridgeProvider: (id) => request('POST', `${orgPrefix()}/bridge/providers/${id}/test`, {}),
  getCrmStatus: () => request('GET', `${orgPrefix()}/crm/status`),
  putCrmConnection: (body) => request('PUT', `${orgPrefix()}/crm/connection`, body),
  syncCrm: (opts = {}) => request('POST', `${orgPrefix()}/crm/sync`, opts),
  crmWriteback: (body) => request('POST', `${orgPrefix()}/crm/writeback`, body),
  crmPendingActivities: () => request('GET', `${orgPrefix()}/crm/activities/pending`),
  crmConfirmActivity: (id, body) => request('POST', `${orgPrefix()}/crm/activities/${id}/confirm`, body),
  crmReconcile: () => request('POST', `${orgPrefix()}/crm/reconcile`, {}),
  bridgeAsk: (body) => request('POST', `${orgPrefix()}/bridge/ask`, body),
  bridgePageAsk: (body) => request('POST', `${orgPrefix()}/bridge/page-ask`, body),
  bridgeBrief: (body) => request('POST', `${orgPrefix()}/bridge/brief`, body),
  planStatus: () => request('GET', `${orgPrefix()}/plan/status`),
  listPlanVersions: () => request('GET', `${orgPrefix()}/plan/versions`),
  putPlanAutopilot: (body) => request('PUT', `${orgPrefix()}/plan/autopilot`, body),
  planRestudy: (body = {}) => request('POST', `${orgPrefix()}/plan/restudy`, body),
  rollbackPlanVersion: (id, reason) =>
    request('POST', `${orgPrefix()}/plan/versions/${id}/rollback`, { reason }),
  listStrategyTemplates: () => request('GET', `${orgPrefix()}/strategy/templates`),
  getStrategyView: () => request('GET', `${orgPrefix()}/strategy/view`),
  getStrategyDefinition: () => request('GET', `${orgPrefix()}/strategy/definition`),
  selectStrategyTemplate: (body) => request('POST', `${orgPrefix()}/strategy/select-template`, body),
  approveStrategy: (body = {}) => request('POST', `${orgPrefix()}/strategy/approve`, body),
  previewStrategyCsv: (body) => request('POST', `${orgPrefix()}/strategy/import/csv/preview`, body),
  importStrategyCsv: (body) => request('POST', `${orgPrefix()}/strategy/import/csv`, body),
  getJiraStrategyStatus: () => request('GET', `${orgPrefix()}/strategy/jira`),
  putJiraStrategyConnection: (body) => request('PUT', `${orgPrefix()}/strategy/jira`, body),
  importStrategyJira: (body = {}) => request('POST', `${orgPrefix()}/strategy/import/jira`, body),
  enrichStrategy: (body = {}) => request('POST', `${orgPrefix()}/strategy/enrich`, body),
  migrateStrategy: (body = {}) => request('POST', `${orgPrefix()}/strategy/migrate`, body),

  planOsPlanTypes: () => request('GET', `${orgPrefix()}/plan-os/plan-types`),
  planOsSyncNexusContext: (objects) =>
    request('POST', `${orgPrefix()}/plan-os/nexus-context/sync`, { objects }),
  planOsSyncNexusContextCenter: (opts = {}) =>
    request('POST', `${orgPrefix()}/plan-os/nexus-context/sync`, opts),
  planOsMigrationBackfillPipelines: () =>
    request('POST', `${orgPrefix()}/plan-os/migration/backfill-pipelines`, {}),
  planOsListNexusContext: () => request('GET', `${orgPrefix()}/plan-os/nexus-context`),
  planOsGetContext: () => request('GET', `${orgPrefix()}/plan-os/context`),
  planOsPutContext: (context) => request('PUT', `${orgPrefix()}/plan-os/context`, { context }),
  planOsContextPack: () => request('GET', `${orgPrefix()}/plan-os/context/pack`),
  planOsContextDrift: () => request('GET', `${orgPrefix()}/plan-os/context/drift`),
  planOsResolveDrift: (decision) =>
    request('POST', `${orgPrefix()}/plan-os/context/drift/resolve`, { decision }),
  planOsGetContract: () => request('GET', `${orgPrefix()}/plan-os/contract`),
  planOsPutContract: (contract) => request('PUT', `${orgPrefix()}/plan-os/contract`, { contract }),
  planOsCompile: (overrides = []) =>
    request('POST', `${orgPrefix()}/plan-os/contract/compile`, { overrides }),
  planOsActivate: (overrides = []) =>
    request('POST', `${orgPrefix()}/plan-os/contract/activate`, { overrides }),
  planOsRevise: (patch) => request('POST', `${orgPrefix()}/plan-os/contract/revise`, { patch }),
  planOsContractVersions: () => request('GET', `${orgPrefix()}/plan-os/contract/versions`),
  planOsDerive: (truth) => request('POST', `${orgPrefix()}/plan-os/engine/derive`, { truth }),
  planOsAiContext: (body = {}) => request('POST', `${orgPrefix()}/plan-os/ai/context`, body),
  planOsMigrationAnalyze: () => request('GET', `${orgPrefix()}/plan-os/migration/analyze`),
  planOsMigrationPropose: () => request('POST', `${orgPrefix()}/plan-os/migration/propose-draft`, {}),

  uploadProfilePicture: async (blob, contentType) => {
    const path = `${orgPrefix()}/profile/picture`;
    let res;
    try {
      res = await fetch(`${BASE}${path}`, {
        method: 'PUT',
        headers: {
          ...sessionHeaders(),
          'content-type': contentType,
        },
        body: blob,
      });
    } catch (err) {
      throw new ApiError(0, 'NETWORK', `Cannot reach the Bridge45 API: ${err.message}`);
    }
    if (res.status === 401 && loadSession()?.refreshToken) {
      try {
        await refreshSession();
        res = await fetch(`${BASE}${path}`, {
          method: 'PUT',
          headers: {
            ...sessionHeaders(),
            'content-type': contentType,
          },
          body: blob,
        });
      } catch {
        clearSession();
        throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — sign in again');
      }
    }
    return parseResponse(res, 'PUT', path);
  },
};

export function fireCrmWriteback(body) {
  if (!body) return Promise.resolve(null);
  return api.crmWriteback(body).catch(() => null);
}

export { BASE as API_BASE };
